import { useCallback, useMemo } from 'react';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import { taskV2ActionsAtom, taskV2FocusedIndexAtom, taskV2TimelineScaleAtom } from '../atoms/v3/taskV2Atoms';
import { undoToastAtom, executeUndoAtom } from '../atoms/v3/undoToastAtoms';
import { isTouchDevice } from '../utils/platformUtils';

/**
 * TaskV2 页面快捷键业务逻辑 Hook
 * 提供所有 TaskV2 页面快捷键的具体实现函数
 */
export const useTaskV2ShortcutActions = () => {
  const getActions = useSetAtom(taskV2ActionsAtom);
  const actions = getActions();
  const undoToastData = useAtomValue(undoToastAtom);
  const executeUndo = useAtomValue(executeUndoAtom);
  const [timelineScale, setTimelineScale] = useAtom(taskV2TimelineScaleAtom);

  // 调试：检查 actions 是否正确获取
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 TaskV2ShortcutActions: actions object:', actions);
    console.log('🔍 TaskV2ShortcutActions: focusPrevTask available:', !!actions?.focusPrevTask);
    console.log('🔍 TaskV2ShortcutActions: focusNextTask available:', !!actions?.focusNextTask);
  }

  // 获取当前聚焦的任务
  const getFocusedTask = useCallback(() => {
    return actions?.getFocusedTask?.();
  }, [actions]);

  // 获取滚动容器
  const getScrollContainer = useCallback(() => {
    // 查找滚动容器，优先使用 data 属性
    const selectors = [
      '[data-scroll-container]',
      '.task-list-container',
      '.overflow-auto',
      'main'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        // 对于 data-scroll-container，直接返回（这是我们明确标记的滚动容器）
        if (selector === '[data-scroll-container]') {
          console.log('🔥 TaskV2: Found scroll container via data attribute');
          return element;
        }
        // 对于其他选择器，检查是否有滚动能力
        if (element.scrollHeight > element.clientHeight) {
          console.log('🔥 TaskV2: Found scroll container via selector:', selector);
          return element;
        }
      }
    }

    console.warn('🚨 TaskV2: No scroll container found');
    return null;
  }, []);

  // 导航快捷键
  const goToPrevDay = useCallback(() => {
    console.log('🔥 TaskV2: Go to previous day - SHORTCUT TRIGGERED!');
    actions?.goToPrevDay?.();
  }, [actions]);

  const goToNextDay = useCallback(() => {
    console.log('🔥 TaskV2: Go to next day - SHORTCUT TRIGGERED!');
    actions?.goToNextDay?.();
  }, [actions]);

  const goToToday = useCallback(() => {
    console.log('🔥 TaskV2: Go to today - SHORTCUT TRIGGERED!');
    actions?.goToToday?.();
  }, [actions]);

  // 焦点和滚动快捷键
  const focusPrevTask = useCallback(() => {
    console.log('🔥 TaskV2: Focus previous task - SHORTCUT TRIGGERED!');

    // 检查当前视图模式
    const viewModeElement = document.querySelector('[data-view-mode]');
    const viewMode = viewModeElement?.getAttribute('data-view-mode') || 'list';

    if (viewMode === 'timeline') {
      // 时间轴模式：向上滚动页面
      const scrollContainer = getScrollContainer();
      if (scrollContainer) {
        console.log('🔥 TaskV2: Scrolling up in timeline mode, current scrollTop:', scrollContainer.scrollTop);
        scrollContainer.scrollBy({
          top: -200,
          behavior: 'smooth'
        });
        console.log('🔥 TaskV2: After scroll command, scrollTop should change');
      } else {
        console.warn('🚨 TaskV2: No scroll container found for timeline scroll up');
      }
    } else {
      // 列表模式：选中上一个任务
      console.log('🔥 TaskV2: Focusing previous task in list mode');
      actions?.focusPrevTask?.();
    }
  }, [actions, getScrollContainer]);

  const focusNextTask = useCallback(() => {
    console.log('🔥 TaskV2: Focus next task - SHORTCUT TRIGGERED!');

    // 检查当前视图模式
    const viewModeElement = document.querySelector('[data-view-mode]');
    const viewMode = viewModeElement?.getAttribute('data-view-mode') || 'list';

    if (viewMode === 'timeline') {
      // 时间轴模式：向下滚动页面
      const scrollContainer = getScrollContainer();
      if (scrollContainer) {
        console.log('🔥 TaskV2: Scrolling down in timeline mode, current scrollTop:', scrollContainer.scrollTop);
        scrollContainer.scrollBy({
          top: 200,
          behavior: 'smooth'
        });
        console.log('🔥 TaskV2: After scroll command, scrollTop should change');
      } else {
        console.warn('🚨 TaskV2: No scroll container found for timeline scroll down');
      }
    } else {
      // 列表模式：选中下一个任务
      console.log('🔥 TaskV2: Focusing next task in list mode');
      actions?.focusNextTask?.();
    }
  }, [actions, getScrollContainer]);

  // 任务操作快捷键
  const toggleTask = useCallback(() => {
    console.log('TaskV2: Toggle task');
    const focusedTask = getFocusedTask();
    if (focusedTask) {
      // 优先模拟点击圆圈，确保触发相同的逻辑
      const focusedTaskElement = document.querySelector(`[data-task-id="${focusedTask.id}"]`);
      const checkboxButton = focusedTaskElement?.querySelector('button');
      if (checkboxButton) {
        checkboxButton.click();
      } else {
        // 如果找不到按钮，回退到直接调用
        console.warn('TaskV2: Could not find checkbox button, using fallback');
        // 这里需要从外部传入 handleTaskToggle 函数
        // 暂时使用 actions 中的方法
        if (actions?.toggleTask) {
          actions.toggleTask(focusedTask.id, !focusedTask.done);
        }
      }
    }
  }, [getFocusedTask, actions]);

  const editTask = useCallback(() => {
    console.log('🔥 TaskV2: Edit task - SHORTCUT TRIGGERED!');
    const focusedTask = getFocusedTask();
    console.log('🔍 TaskV2: Focused task:', focusedTask);

    if (focusedTask) {
      // 优先通过点击任务元素来触发编辑（保持UI一致性）
      const focusedTaskElement = document.querySelector(`[data-task-id="${focusedTask.id}"]`);
      console.log('🔍 TaskV2: Found task element:', !!focusedTaskElement);

      if (focusedTaskElement) {
        console.log('🔥 TaskV2: Clicking task element to edit');
        // 确保点击的是任务内容区域，而不是复选框
        const taskContentArea = focusedTaskElement.querySelector('.flex-1') || focusedTaskElement;
        (taskContentArea as HTMLElement).click();
      } else {
        console.warn('🚨 TaskV2: Task element not found, cannot edit');
      }
    } else {
      console.warn('🚨 TaskV2: No focused task found. Use arrow keys to select a task first.');
    }
  }, [getFocusedTask]);

  const newTask = useCallback(() => {
    console.log('🔥 TaskV2: New task - SHORTCUT TRIGGERED!');
    if (!isTouchDevice()) {
      // 查找新建任务按钮并点击（保持UI一致性）
      const newTaskButton = document.querySelector('[data-new-task-button]') as HTMLElement;
      console.log('🔍 TaskV2: Found new task button:', !!newTaskButton);

      if (newTaskButton) {
        console.log('🔥 TaskV2: Clicking new task button');
        newTaskButton.click();
      } else {
        // 尝试查找其他可能的新建任务按钮
        const fabButton = document.querySelector('.fab-button') as HTMLElement;
        const plusButton = document.querySelector('[aria-label*="新建"], [aria-label*="添加"], [title*="新建"], [title*="添加"]') as HTMLElement;

        if (fabButton) {
          console.log('🔥 TaskV2: Clicking FAB button');
          fabButton.click();
        } else if (plusButton) {
          console.log('🔥 TaskV2: Clicking plus button');
          plusButton.click();
        } else {
          console.warn('🚨 TaskV2: No new task button found');
        }
      }
    }
  }, []);

  // 视图模式快捷键
  const toggleViewMode = useCallback(() => {
    console.log('TaskV2: Toggle view mode');
    
    // 检查是否在特殊模式下
    const specialModeElement = document.querySelector('[data-special-mode]');
    const specialMode = specialModeElement?.getAttribute('data-special-mode') === 'true';
    
    if (!specialMode) {
      actions?.toggleViewMode?.();
    }
  }, [actions]);

  // 重置焦点
  const resetFocus = useCallback(() => {
    console.log('TaskV2: Reset focus');
    actions?.resetFocus?.();
  }, [actions]);

  // 撤销操作
  const undo = useCallback(() => {
    console.log('TaskV2: Undo');
    if (undoToastData && executeUndo) {
      executeUndo((taskId: string, previousState: boolean) => {
        // 这里需要从外部传入 handleUndo 函数
        console.log('TaskV2: Executing undo for task', taskId, 'to state', previousState);
        if (actions?.undo) {
          actions.undo(taskId, previousState);
        }
      });
    }
  }, [undoToastData, executeUndo, actions]);

  // 时间轴缩放快捷键
  const zoomIn = useCallback(() => {
    console.log('TaskV2: Zoom in timeline');
    
    // 检查当前视图模式和特殊模式
    const viewModeElement = document.querySelector('[data-view-mode]');
    const viewMode = viewModeElement?.getAttribute('data-view-mode') || 'list';
    const specialModeElement = document.querySelector('[data-special-mode]');
    const specialMode = specialModeElement?.getAttribute('data-special-mode') === 'true';
    
    if (viewMode === 'timeline' && !specialMode) {
      const minScale = 0.5;
      const maxScale = 3.0;
      const step = 0.25;
      const newScale = Math.min(maxScale, timelineScale + step);
      setTimelineScale(newScale);
    }
  }, [timelineScale, setTimelineScale]);

  const zoomOut = useCallback(() => {
    console.log('TaskV2: Zoom out timeline');
    
    // 检查当前视图模式和特殊模式
    const viewModeElement = document.querySelector('[data-view-mode]');
    const viewMode = viewModeElement?.getAttribute('data-view-mode') || 'list';
    const specialModeElement = document.querySelector('[data-special-mode]');
    const specialMode = specialModeElement?.getAttribute('data-special-mode') === 'true';
    
    if (viewMode === 'timeline' && !specialMode) {
      const minScale = 0.5;
      const maxScale = 3.0;
      const step = 0.25;
      const newScale = Math.max(minScale, timelineScale - step);
      setTimelineScale(newScale);
    }
  }, [timelineScale, setTimelineScale]);

  // 检查是否有任务详情弹窗打开
  const isTaskDetailOpen = useCallback(() => {
    const taskDetailModal = document.querySelector('[data-task-detail-modal]');
    return !!taskDetailModal;
  }, []);

  // 关闭任务详情弹窗
  const closeTaskDetail = useCallback(() => {
    console.log('TaskV2: Close task detail');
    if (isTaskDetailOpen()) {
      // 查找关闭按钮并点击
      const closeButton = document.querySelector('[data-task-detail-close]') as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        // 回退到 ESC 键事件
        const escEvent = new KeyboardEvent('keydown', { key: 'Escape' });
        document.dispatchEvent(escEvent);
      }
    }
  }, [isTaskDetailOpen]);

  return useMemo(() => ({
    // 导航
    goToPrevDay,
    goToNextDay,
    goToToday,

    // 焦点和滚动
    focusPrevTask,
    focusNextTask,

    // 任务操作
    toggleTask,
    editTask,
    newTask,

    // 视图模式
    toggleViewMode,

    // 其他
    resetFocus,
    undo,
    zoomIn,
    zoomOut,
    closeTaskDetail,

    // 状态检查
    isTaskDetailOpen,
    getFocusedTask
  }), [
    goToPrevDay,
    goToNextDay,
    goToToday,
    focusPrevTask,
    focusNextTask,
    toggleTask,
    editTask,
    newTask,
    toggleViewMode,
    resetFocus,
    undo,
    zoomIn,
    zoomOut,
    closeTaskDetail,
    isTaskDetailOpen,
    getFocusedTask
  ]);
};
